import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DashboardLayout from '../components/DashboardLayout';
import { useNavigate } from 'react-router-dom';
import { getBlogs, deleteBlog } from '../services/blogService';
import AdminEmptyState from '../components/AdminEmptyState';
import AdminPageHeader from '../components/AdminPageHeader';
import AdminPagination from '../components/AdminPagination';
import AdminSection from '../components/AdminSection';
import useAdminPagination from '../hooks/useAdminPagination';

function BlogList() {
  const [blogs, setBlogs] = useState([]);
  const [error, setError] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const {
    page,
    rowsPerPage,
    count,
    paginatedItems: paginatedBlogs,
    handlePageChange,
    handleRowsPerPageChange,
  } = useAdminPagination(blogs, 10);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      const data = await getBlogs();
      setBlogs(Array.isArray(data) ? data : []);
      setError('');
    } catch (fetchError) {
      console.error('Error fetching blogs:', fetchError);
      setBlogs([]);
      setError('Failed to load blog posts.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) {
      return;
    }

    try {
      await deleteBlog(id);
      fetchBlogs();
    } catch (deleteError) {
      console.error('Error deleting blog post:', deleteError);
      setError('Failed to delete blog post.');
    }
  };

  return (
    <DashboardLayout>
      <Stack spacing={3}>
        <AdminPageHeader
          eyebrow="Content"
          title="Blog Posts"
          description="Manage published articles, author assignments, and post metadata from one clean overview."
          meta={<Chip label={`${blogs.length} posts`} variant="outlined" />}
          action={
            <Button variant="contained" onClick={() => navigate('/blogs/create')}>
              Create Blog Post
            </Button>
          }
        />

        {error ? <Alert severity="error">{error}</Alert> : null}

        {isMobile ? (
          blogs.length ? (
            <Grid container spacing={2}>
              {paginatedBlogs.map((blog) => (
                <Grid item xs={12} key={blog.blogId}>
                  <AdminSection>
                    <Stack spacing={2}>
                      <Stack direction="row" justifyContent="space-between" spacing={2}>
                        <Stack spacing={0.75}>
                          <Typography variant="h6">{blog.title}</Typography>
                          <Typography color="text.secondary">
                            {blog.author?.name || 'No author'}
                          </Typography>
                        </Stack>
                        <Chip label={blog.category || 'Uncategorized'} size="small" variant="outlined" />
                      </Stack>
                      <Typography color="text.secondary">
                        {blog.publishDate
                          ? new Date(blog.publishDate).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })
                          : 'No publish date'}
                      </Typography>
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button variant="outlined" onClick={() => navigate(`/blogs/edit/${blog.blogId}`)}>
                          Edit
                        </Button>
                        <IconButton color="error" onClick={() => handleDelete(blog.blogId)}>
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </AdminSection>
                </Grid>
              ))}
            </Grid>
          ) : (
            <AdminSection>
              <AdminEmptyState
                title="No blog posts yet"
                description="Create your first article to start building the editorial library."
              />
            </AdminSection>
          )
        ) : (
          <AdminSection sx={{ p: 0, overflow: 'hidden' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Author</TableCell>
                  <TableCell>Publish Date</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {blogs.length ? (
                  paginatedBlogs.map((blog) => (
                    <TableRow key={blog.blogId}>
                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography sx={{ fontWeight: 600 }}>{blog.title}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {blog.slug}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>{blog.category || '-'}</TableCell>
                      <TableCell>{blog.author?.name || '-'}</TableCell>
                      <TableCell>
                        {blog.publishDate ? new Date(blog.publishDate).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          variant="outlined"
                          onClick={() => navigate(`/blogs/edit/${blog.blogId}`)}
                          sx={{ mr: 1 }}
                        >
                          Edit
                        </Button>
                        <IconButton color="error" onClick={() => handleDelete(blog.blogId)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <AdminEmptyState
                        title="No blog posts yet"
                        description="Create your first article to start building the editorial library."
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <AdminPagination
              count={count}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </AdminSection>
        )}
      </Stack>
    </DashboardLayout>
  );
}

export default BlogList;
